import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const perPage = 8;

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const comics = getComicsByCategory(activeSlug);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const totalPages = Math.ceil(sortedComics.length / perPage);
  const paginatedComics = sortedComics.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    setCurrentPage(1);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb />

        {/* Category Header */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                <i className={`${currentCategory?.icon || 'ri-book-open-line'} text-primary-600 text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {currentCategory?.name || '漫画'}分类
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">
                  {currentCategory?.description || '浏览韩国漫画作品'}
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1 text-xs opacity-70">({cat.count})</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Sort & Content */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{comics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                {[
                  { key: 'latest' as const, label: '最新' },
                  { key: 'popular' as const, label: '最热' },
                  { key: 'rating' as const, label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => { setSortBy(s.key); setCurrentPage(1); }}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-foreground-900 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comic Grid */}
            {paginatedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {paginatedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="large" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-open-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画作品</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                      p === currentPage
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}